import { DeleteOutlined, InfoCircleFilled } from '@ant-design/icons';
import { Popover, Space, Row, Col, Button, notification, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import styles from './DeleteDbParametersPopover.module.less';
import DbParameterGroupService from '../../services/DbParameterGroupService';
import CcxIconCheckCircleTwoTone from '../ccx/icons/CcxIconCheckCircleTwoTone';
import CcxIconCloseCircleTwoTone from '../ccx/icons/CcxIconCloseCircleTwoTone';
import CcxIconInfoCircleOutlined from '../ccx/icons/CcxIconInfoCircleOutlined';

interface DeleteDbParametersPopoverProps {
    record: any;
    testId?: string;
    refresh: () => void | Promise<void>;
    isDeleteable: boolean;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const DeleteDbParametersPopover = ({
    record,
    refresh,
    testId = 'DeleteDbParametersPopover',
    isDeleteable,
    setIsLoading,
}: DeleteDbParametersPopoverProps) => {
    const [openPopover, setOpenPopover] = useState(false);

    useEffect(() => {
        setOpenPopover(isDeleteable);
    }, [isDeleteable]);

    const handleCancel = () => {
        setOpenPopover(!openPopover);
    };

    const handleDelete = async () => {
        setIsLoading(true);
        handleCancel();
        try {
            await DbParameterGroupService.deleteDbParameterGroup(record.uuid);
            notification.open({
                message: 'Success',
                description: 'Parameter group deleted successfully',
                icon: <CcxIconCheckCircleTwoTone twoToneColor="#52c41a" />,
                duration: 4.5,
            });
            refresh();
        } catch (e) {
            notification.open({
                message: 'Error',
                description: 'Failed to delete parameter group',
                icon: <CcxIconCloseCircleTwoTone twoToneColor="#eb2f96" />,
                duration: 4.5,
            });
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Popover
            open={openPopover}
            placement="left"
            content={
                <>
                    <Row justify={'end'} className={styles.PopoverContentRow}>
                        <Col span={2}>
                            <InfoCircleFilled
                                className={styles.ExclamationCircleFilledColor}
                            />
                        </Col>
                        <Col span={22}>Are you sure delete this group? </Col>
                    </Row>
                    <Row justify={'end'} className={styles.PopoverActionsRow}>
                        <Space>
                            <Button
                                type="primary"
                                onClick={() => handleDelete()}
                            >
                                Yes
                            </Button>
                            <Button onClick={() => handleCancel()}>No</Button>
                        </Space>
                    </Row>
                </>
            }
            trigger="click"
        >
            <Button
                type="link"
                className={
                    !!record?.dataStores?.length
                        ? styles.DisabledDeleteDbParametersPopoverButton
                        : styles.DeleteDbParametersPopoverButton
                }
                icon={<DeleteOutlined />}
                disabled={record?.dataStores?.length > 0}
                onClick={() => setOpenPopover(!openPopover)}
            >
                Delete
            </Button>
            {record.dataStores?.length > 0 && (
                <Tooltip
                    overlayInnerStyle={{ maxWidth: '400px' }}
                    title="The delete option is disabled because this DB parameter group
                is assigned to a data store. To delete it, unassign the group fromall 
                associated data stores."
                >
                    <Button type="text" icon={<CcxIconInfoCircleOutlined />} />
                </Tooltip>
            )}
        </Popover>
    );
};

export default DeleteDbParametersPopover;
